<template>
    <!-- fixed inset-0: the menu is sized by the viewport, not the page, so the
         nav area below can scroll internally while body scrolling is locked. -->
    <section
        name="menu"
        class="py-md gap-sm bg-lightest fixed inset-0 z-30 flex flex-col"
        data-test="mobile-menu"
    >
        <div class="gap-md px-sm pb-md flex flex-row-reverse items-center" @click="$emit('close')">
            <i class="i-exit h-6 w-6 cursor-pointer" />
        </div>
        <div class="gap-lg nav-area flex min-h-0 flex-1 flex-col overflow-y-auto">
            <SideMenuDropdown
                v-for="route in mobileMenuRoutes"
                :key="route.title"
                :category="route.title"
                :icon="route.icon"
                :links="route.children.length ? route.children : [{ title: route.title, to: route.to ?? route.title }]"
                :mobile="true"
                @close="$emit('close')"
            />
        </div>
        <UserProfile class="mx-sm mt-auto" @close="$emit('close')" />
    </section>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue';
import SideMenuDropdown from '../misc/side-menu-dropdown.vue';
import { useAdminStore } from '@/stores/admin';
import { useOrganizationStore } from '@/stores/organization';
import UserProfile from '../user-profile.vue';
import { routes } from '@/router'; // use the exported array
import { getVisibleAdminMenuSections } from '@/router/admin-access';

defineEmits<{ close: [] }>();

const adminStore = useAdminStore();
const organizationStore = useOrganizationStore();
const usersOrganization = computed(() => organizationStore.usersOrganization);
const user = computed(() => organizationStore.user);
const isAdminView = computed(() => localStorage.getItem('admin') === 'true');

onMounted(() => {
    document.body.style.overflow = 'hidden';
});

onUnmounted(() => {
    document.body.style.overflow = 'auto';
});

function userHasRole(requiredRoles?: string[]) {
    if (!requiredRoles || requiredRoles.length === 0) return true; // no restriction
    if (!user.value || !usersOrganization.value) return false;

    // org-admin shortcut
    const roles =
        user.value.organizations
            .find((org) => org.organization_id === usersOrganization.value?.organization_id)
            ?.organization_roles.map((r) => r.name) ?? [];

    return roles.includes('org-admin') || requiredRoles.some((r) => roles.includes(r));
}

const organizationMobileMenuRoutes = computed(() => {
    // No readyForMobile gating: every page the user has access to is reachable
    // on mobile. Hiding a page guarantees a bad mobile experience, while an
    // unpolished layout at least lets people reach their data — the mobile
    // epic (ITDEVELOP-3079) improves those layouts rather than hiding them.
    return routes
        .filter((r) => r.meta?.icon)
        .filter((r) => !r.meta?.hideInMobileMenu)
        .filter((r) => userHasRole(r.meta?.allowedRoles as string[]))
        .map((r) => {
            const menuChildren =
                (r.children || [])
                    .filter((c) => !c.meta?.hideInMobileMenu)
                    .filter(
                        (c) =>
                            userHasRole(c.meta?.allowedRoles as string[]) && c.meta?.menuLink && !c.path.includes(':'),
                    )
                    .map((c) => {
                        // Remove everything from ':' and after in c.path
                        const cleanPath = typeof c.path === 'string' ? c.path.split(':')[0] : c.path;
                        return {
                            title:
                                c.meta?.title ??
                                ((c.name || cleanPath) as string).charAt(0).toUpperCase() +
                                    ((c.name || cleanPath) as string).slice(1),
                            to: (c.name ?? c.path) as string,
                        };
                    }) ?? [];

            return {
                title:
                    r.meta?.title ??
                    ((r.name ?? r.path) as string).charAt(0).toUpperCase() + ((r.name ?? r.path) as string).slice(1),
                to: (r.name ?? r.path) as string, //? r.name as string : r.path as string,
                icon: r.meta?.icon as string,
                children: menuChildren,
            };
        });
});

const adminMobileMenuRoutes = computed(() => {
    return getVisibleAdminMenuSections(adminStore.adminRoleContext).map((section) => ({
        title: section.category ?? section.links[0]?.title ?? '',
        to: section.links[0]?.to,
        icon: section.icon,
        children: section.links,
    }));
});

const mobileMenuRoutes = computed(() => {
    return isAdminView.value ? adminMobileMenuRoutes.value : organizationMobileMenuRoutes.value;
});
</script>

<style scoped>
@reference '@/assets/styles/default.css';
.nav-area {
    @apply gap-sm flex flex-col;
    .router-link {
        @apply gap-sm px-sm py-sm flex items-center rounded-sm;
        transition: background-color 0.3s ease;

        p {
            @apply text-md font-bold;
        }

        i {
            @apply h-6 w-6;
        }

        &:hover,
        &:active {
            background-color: var(--bg-primary);
        }
    }
}
</style>
